import React, { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import LoginSecurityCode from "@components/login_security_code";
import LoginEmail from "@components/login_email";
import GreenhouseLogo from "@images/greenhouse_logo.svg";
import useTranslation from "@hooks/use_translation";
import Layout from "@components/layout";
import { type SocialLogin } from "@components/social_login_buttons";
import IdentityVerificationLogo from "@/components/ui/identity_verification_logo";
import {
  identityVerificationPartnerConfig,
  isIdentityVerificationPartner,
} from "@/utils/identity_verification_provider";

import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";
import { authTrackingPayload, type AuthTracking } from "@/utils/auth_tracking";

import "./login.scss";
import { Body, Link } from "@/sprout/typography";

let pageShownAttemptId: string | null = null;

type Props = {
  jobBoardToken: string;
  createPath: string;
  submitCodePath: string;
  ghrUrl: string;
  loginError?: string | null;
  socialLogin: SocialLogin;
  identityVerificationPartner?: string | null;
  identityVerificationCompanyName?: string | null;
  authTracking?: AuthTracking | null;
};

const SPLASH_KEYS = {
  copy_b: "login.splash_copy_b",
  copy_c: "login.splash_copy_c",
} as const;

const Login = (props: Props) => {
  const {
    jobBoardToken,
    createPath,
    submitCodePath,
    ghrUrl,
    loginError,
    socialLogin,
    identityVerificationPartner,
    identityVerificationCompanyName,
    authTracking,
  } = props;
  const { t } = useTranslation("misc");
  const { trackEvent } = useSnowplowTracker();

  useEffect(() => {
    const attemptId = authTracking?.signInAttemptId ?? null;
    if (attemptId !== null && attemptId === pageShownAttemptId) {
      return;
    }

    pageShownAttemptId = attemptId;
    trackEvent(SnowplowEvent.AuthPageShown, authTrackingPayload(authTracking));
  }, [trackEvent, authTracking]);

  // Branding renders only from server-verified props; query params would let a
  // crafted link paint an arbitrary company onto the login screen.
  const partner = isIdentityVerificationPartner(identityVerificationPartner)
    ? identityVerificationPartner
    : null;
  const companyName = identityVerificationCompanyName || "";
  const identityVerificationConfig =
    partner && companyName ? identityVerificationPartnerConfig(partner) : null;

  const variant = authTracking?.variant;
  const splashKey =
    variant === "copy_b" || variant === "copy_c" ? SPLASH_KEYS[variant] : "login.splash";

  const [showCode, setShowCode] = useState(false);
  const [email, setEmail] = useState<string>("");

  return (
    <div className="login">
      <div className="login__left">
        <div className="login__leaf"></div>
      </div>
      <div className="login__right">
        <div className="login__content-wrapper">
          <div className="login__content">
            {identityVerificationConfig ? (
              <>
                <div className="flex items-center justify-center mb-6 w-full">
                  <img src={GreenhouseLogo} alt="MyGreenhouse Logo" className="h-10 w-auto" />
                  <div className="h-10 w-[2px] bg-gray-800 mx-8" />
                  <IdentityVerificationLogo partner={identityVerificationConfig.partner} />
                </div>
                <div className="login__heading">
                  {t("login_identity_verification.title", {
                    provider_name: identityVerificationConfig.displayName,
                  })}
                </div>
                <div className="login__subheader">
                  <div>
                    {t("login_identity_verification.description", {
                      company_name: companyName,
                      provider_name: identityVerificationConfig.displayName,
                    })}
                  </div>
                </div>
              </>
            ) : (
              <>
                <img src={GreenhouseLogo} alt="Greenhouse logo" height="40" />
                <div
                  className="login__heading"
                  dangerouslySetInnerHTML={{ __html: t(splashKey) }}
                />
                <div className="login__subheader">
                  <div>{t("login.subheader")}</div>
                </div>
              </>
            )}

            {loginError && (
              <p
                className="login__error helper-text helper-text--error"
                role="alert"
                data-provides="login-error"
              >
                {loginError}
              </p>
            )}

            {showCode ? (
              <LoginSecurityCode
                submitCodePath={submitCodePath}
                email={email}
                setShowCode={setShowCode}
              />
            ) : (
              <LoginEmail
                jobBoardToken={jobBoardToken}
                createPath={createPath}
                socialLogin={socialLogin}
                setShowCode={setShowCode}
                email={email}
                setEmail={setEmail}
                authTracking={authTracking}
              />
            )}

            <div className="login__agreement">
              <Body secondary>
                <Trans
                  i18nKey="misc:login:agreement"
                  components={{
                    PrivacyPolicy: (
                      <Link secondary external href={t("terms.privacy_policy.url")}>
                        {t("terms.privacy_policy.text")}
                      </Link>
                    ),
                    UserAgreement: (
                      <Link secondary external href={t("terms.user_agreement.url")}>
                        {t("terms.user_agreement.text")}
                      </Link>
                    ),
                  }}
                />
              </Body>
            </div>

            <div className="login__footer">
              <Body secondary>
                {t("login.ghr")}{" "}
                <Link secondary external href={ghrUrl}>
                  {t("login.signIn")}
                </Link>
              </Body>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Login.layout = (page: React.ReactNode) => <Layout publicFacing>{page}</Layout>;

export default Login;
